import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';

import { TimeConflito } from 'src/app/core/utils/transferencia-times.utils';

/**
 * Modal de confirmacao de "transferencia de times entre gestores".
 *
 * Contexto (BUG-017, regressivo-pos-migracao 2026-07-29):
 *
 * Quando o admin cria/edita gestor atribuindo times que ja pertencem a
 * outros gestores, o backend Node retorna 409 estruturado
 * (`MANAGER.USUARIOS.TIMES_JA_ATRIBUIDOS`) com a lista de conflitos. Este
 * modal exibe os conflitos e coleta a confirmacao — se OK, o consumidor
 * re-submete o payload com `forcarTransferencia: true`.
 *
 * Padrao visual replica o modal de exclusao existente em
 * `aba-hierarquia.component.html` (`excluir-confirm-dialog`) — mesmo
 * radius, mesmo footer, mesma anatomia de botoes — mas com header em
 * gradiente indigo (transferencia nao e destrutiva; nao usa vermelho).
 *
 * Standalone para reuso cross-module (aba-hierarquia + SHARED
 * colaborador-form-modal). Nenhuma dependencia de estado global — o
 * consumidor controla `visible`, `conflitos`, `loading`. Emit `confirmar`
 * dispara o re-submit; `cancelar` apenas fecha.
 */
@Component({
  selector: 'app-transferencia-times-confirm-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule],
  templateUrl: './transferencia-times-confirm-dialog.component.html',
  styleUrls: ['./transferencia-times-confirm-dialog.component.scss']
})
export class TransferenciaTimesConfirmDialogComponent {
  /** Controlado pelo consumidor. `true` = dialog aberto. */
  @Input() visible = false;

  /** Lista de conflitos recebida do backend (via
   * `extractTimesJaAtribuidosConflitos`). Nao pode ser vazia — o helper
   * ja garante isso, mas o template tem guard defensivo mesmo assim. */
  @Input() conflitos: TimeConflito[] = [];

  /** `true` enquanto o re-submit com `forcarTransferencia: true` esta
   * em voo. Bloqueia botoes/close pra evitar double-submit. */
  @Input() loading = false;

  /** Dispara quando o admin confirma. Consumidor re-submete o payload
   * original + `forcarTransferencia: true`. */
  @Output() confirmar = new EventEmitter<void>();

  /** Dispara quando o admin cancela (X ou botao "Cancelar"). Consumidor
   * apenas fecha o dialog — nada acontece no backend. */
  @Output() cancelar = new EventEmitter<void>();

  get pluralConflitos(): boolean {
    return this.conflitos.length > 1;
  }

  onConfirmar(): void {
    if (this.loading) return;
    this.confirmar.emit();
  }

  onCancelar(): void {
    if (this.loading) return;
    this.cancelar.emit();
  }

  trackByTimeId(_index: number, conflito: TimeConflito): number {
    return conflito.timeId;
  }
}
